import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, Truck, Package, Calendar } from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';
import { apiOrigin } from '../lib/site';

const API_URL = apiOrigin();

const STATUS_STEPS = [
  { key: 'Pending',       label: 'Pending',         icon: Clock },
  { key: 'In Production', label: 'In Production',   icon: Package },
  { key: 'Shipped',       label: 'Shipped',         icon: Truck },
  { key: 'Completed',     label: 'Completed',       icon: CheckCircle },
];

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
};

const PublicOrderTracker = () => {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const t = new URLSearchParams(window.location.search).get('t') || '';
        const r = await axios.get(`${API_URL}/api/public/orders/${orderId}/track`, { headers: { 'X-Share-Token': t } });
        setData(r.data);
      } catch (e) {
        setError('Order not found or no longer available.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#cbd5e1' }}>
      Loading order…
    </div>
  );
  if (error || !data) return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', color: '#fff', padding: 32 }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>Order Not Found</h1>
        <p style={{ color: '#cbd5e1', marginTop: 8 }}>{error || 'Please check the link and try again.'}</p>
      </div>
    </div>
  );

  const currentStepIndex = STATUS_STEPS.findIndex((s) => s.key === data.status);
  const safeStepIndex = currentStepIndex >= 0 ? currentStepIndex : 0;
  const isCompleted = data.status === 'Completed';
  const progress = data.production?.percent || 0;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', color: '#e2e8f0' }}>
      <header style={{ padding: '24px 32px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#fbbf24' }}>{brandName()}</h1>
          <span style={{ fontSize: 12, color: '#94a3b8', letterSpacing: 1, textTransform: 'uppercase' }}>Order Tracker</span>
        </div>
      </header>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px' }}>
        {/* Header card */}
        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>Order</p>
          <h2 style={{ fontSize: 32, fontWeight: 800, margin: '6px 0 0', color: '#fff' }}>{data.po_number || data.order_id.slice(0, 8)}</h2>
          <p style={{ fontSize: 16, color: '#cbd5e1', margin: '4px 0 0' }}>{data.customer_name}</p>
          {data.due_date && (
            <div style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 999, padding: '6px 14px' }}>
              <Calendar size={14} color="#fbbf24" />
              <span style={{ fontSize: 13, color: '#fde68a', fontWeight: 600 }}>Due {fmtDate(data.due_date)}</span>
            </div>
          )}
        </div>

        {/* Status timeline */}
        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 18 }}>Status</p>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${STATUS_STEPS.length}, 1fr)`, gap: 8 }}>
            {STATUS_STEPS.map((s, i) => {
              const active = i <= safeStepIndex;
              const Icon = s.icon;
              return (
                <div key={s.key} style={{ textAlign: 'center', position: 'relative' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    background: active ? '#fbbf24' : 'rgba(255,255,255,0.08)',
                    color: active ? '#0f172a' : '#475569',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={20} />
                  </div>
                  <p style={{ fontSize: 11, color: active ? '#fde68a' : '#64748b', fontWeight: 700, margin: '6px 0 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
                </div>
              );
            })}
          </div>

          {/* Production progress bar */}
          {data.production && data.production.total > 0 && !isCompleted && (
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#cbd5e1', marginBottom: 6 }}>
                <span>Production progress</span>
                <span style={{ fontWeight: 700, color: '#fde68a' }}>{progress}% ({data.production.completed} of {data.production.total} tasks done)</span>
              </div>
              <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: 'linear-gradient(90deg, #fbbf24, #f59e0b)', borderRadius: 999 }} />
              </div>
            </div>
          )}

          {isCompleted && data.completed_at && (
            <div style={{ marginTop: 24, padding: 14, background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
              <CheckCircle size={18} color="#86efac" />
              <p style={{ fontSize: 13, color: '#86efac', margin: 0, fontWeight: 600 }}>
                Order completed on {fmtDate(data.completed_at)}
              </p>
            </div>
          )}

          {(data.tracking_number || (data.shipping && data.shipping.status !== 'pending')) && (
            <div style={{ marginTop: 16, padding: 14, background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10 }} data-testid="public-shipping-box">
              <p style={{ fontSize: 11, color: '#bfdbfe', margin: 0, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>Shipping{data.shipping?.status ? ` · ${{ pending: 'Preparing', ready_to_ship: 'Ready to ship', shipped: 'Shipped', delivered: 'Delivered' }[data.shipping.status] || data.shipping.status}` : ''}</p>
              {data.tracking_number && <p style={{ fontSize: 15, color: '#fff', margin: '2px 0 0', fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>{data.shipping?.carrier_label ? `${data.shipping.carrier_label} ` : ''}{data.tracking_number}</p>}
              {data.shipping?.tracking_url && <a href={data.shipping.tracking_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 13, color: '#fff', background: '#2563eb', padding: '6px 12px', borderRadius: 6, textDecoration: 'none', fontWeight: 700 }} data-testid="public-tracking-link">Track package</a>}
            </div>
          )}
        </div>

        {/* Items / Designs */}
        <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24 }}>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 14 }}>
            Items ({data.items?.length || 0} · {data.quantity || 0} pieces)
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {(data.items || []).map((it) => {
              const src = it.thumbnail
                ? (it.thumbnail.startsWith('data:') ? it.thumbnail : `data:image/png;base64,${it.thumbnail}`)
                : null;
              return (
                <div key={it.item_index} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 12 }}>
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '100%', background: 'rgba(255,255,255,0.05)', borderRadius: 8, overflow: 'hidden' }}>
                    {src ? (
                      <img src={src} alt={`${it.brand} ${it.style}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#475569', fontSize: 11 }}>No design</div>
                    )}
                    {it.approval_status === 'approved' && (
                      <span style={{ position: 'absolute', top: 6, right: 6, background: '#22c55e', color: '#0f172a', fontSize: 9, fontWeight: 800, padding: '2px 6px', borderRadius: 999 }}>✓ APPROVED</span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', margin: '8px 0 0' }}>{it.brand} {it.style}</p>
                  <p style={{ fontSize: 12, color: '#cbd5e1', margin: '2px 0 0' }}>{it.color} · ×{it.quantity}</p>
                  {it.decoration_notes && (
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0', lineHeight: 1.4 }}>{it.decoration_notes}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 28 }}>
          Questions? Reply to your last email from {brandName()}.
        </p>
      </main>
    </div>
  );
};

export default PublicOrderTracker;
